import { useMemo, type Ref } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleAlert, CircleCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buildDraftSummarySections } from '@/lib/cdds-draft-summary'
import { buildWizardDraftPatch, type WizardFormValues } from '@/lib/wizard-draft'
import {
  AGREEMENT_SECTION_KEYS,
  type AgreementSectionKey,
} from '@/lib/agreement-section-completion'

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/* Draft dates are stored as ISO strings; show them the way the client typed them. */
function displayValue(value: string, locale: string) {
  const m = ISO_DATE.exec(value)
  if (!m) return value
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : 'id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}

interface AgreementReviewProps {
  values: WizardFormValues
  completedSections: Record<AgreementSectionKey, boolean>
  onEdit: (section: AgreementSectionKey) => void
  /** Read-only draft: the per-section action reads "Lihat" instead of "Ubah". */
  locked: boolean
  headingRef: Ref<HTMLHeadingElement>
}

/**
 * Read-only summary before submit. Rows come from the same builder CDDS.04 uses, so the
 * client sees the data exactly as the application form will show it.
 */
export function AgreementReview({ values, completedSections, onEdit, locked, headingRef }: AgreementReviewProps) {
  const { t, i18n } = useTranslation()

  const rowsBySection = useMemo(() => {
    const summary = buildDraftSummarySections(
      t,
      buildWizardDraftPatch(values) as unknown as Record<string, unknown>,
    )
    const map = {} as Record<AgreementSectionKey, { label: string; value: string }[]>
    for (const key of AGREEMENT_SECTION_KEYS) {
      const title = t(`form.sections.${key}`)
      map[key] = summary.find((section) => section.title === title)?.rows ?? []
    }
    return map
  }, [t, values])

  return (
    <section className="prod-section prod-review" aria-labelledby="agreement-review-title">
      <header className="prod-section__header">
        <h2 id="agreement-review-title" ref={headingRef} tabIndex={-1} className="prod-section__title">
          {t('agreement.reviewTitle')}
        </h2>
        <p className="prod-section__hint">
          {locked ? t('agreement.reviewHintLocked') : t('agreement.reviewHint')}
        </p>
      </header>
      <ol className="prod-review__list">
        {AGREEMENT_SECTION_KEYS.map((key, index) => {
          const title = t(`form.sections.${key}`)
          const complete = completedSections[key]
          const rows = rowsBySection[key]
          return (
            <li key={key} className="prod-review__item">
              <div className="prod-review__head">
                <h3 className="prod-review__title">
                  <span className="prod-section__num" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  {title}
                </h3>
                <button
                  type="button"
                  className="prod-btn prod-btn--outline prod-btn--compact prod-review__edit"
                  onClick={() => onEdit(key)}
                >
                  {locked ? t('agreement.reviewView') : t('agreement.reviewEdit')}
                  <span className="sr-only">: {title}</span>
                </button>
              </div>
              <p className={cn('prod-review__status', complete ? 'prod-review__status--ok' : 'prod-review__status--todo')}>
                {complete ? (
                  <CircleCheck className="size-4" aria-hidden="true" />
                ) : (
                  <CircleAlert className="size-4" aria-hidden="true" />
                )}
                {complete ? t('agreement.reviewComplete') : t('agreement.reviewIncomplete')}
              </p>
              {rows.length > 0 ? (
                <dl className="prod-cdds-summary__list prod-review__rows">
                  {rows.map((row) => (
                    <div key={row.label} className="prod-cdds-summary__row">
                      <dt>{row.label}</dt>
                      <dd>{displayValue(row.value, i18n.language)}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="prod-review__empty">{t('agreement.reviewEmpty')}</p>
              )}
            </li>
          )
        })}
      </ol>
    </section>
  )
}
